import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { contentReviewerNudgeI18n } from '../i18n.ts';
import { useEditorDocumentSnapshot } from '../hooks/useEditorDocumentSnapshot.ts';
import { countWordsInAdf } from '../utils/countWords.ts';

import type { useContentReviewerNudgeOnCriteria_content$key } from './__generated__/useContentReviewerNudgeOnCriteria_content.graphql.ts';
import { useContentReviewerNudgeOnCriteriaBaseEligibility } from './useContentReviewerNudgeOnCriteriaBaseEligibility.ts';

const TRIGGERING_CONTENT_STATE_NAME = 'Ready for review';

// The page must have at least this many words to be a meaningful review target.
const MIN_WORD_COUNT = 500;

interface UseContentReviewerNudgeOnCriteriaProps {
	contentFragmentRef?: useContentReviewerNudgeOnCriteria_content$key | null;
}

/**
 * OnCriteria trigger detection for the Content Reviewer nudge.
 */
export const useContentReviewerNudgeOnCriteria = ({
	contentFragmentRef,
}: UseContentReviewerNudgeOnCriteriaProps): boolean => {
	const isEligible = useContentReviewerNudgeOnCriteriaBaseEligibility();
	const { formatMessage } = useIntl();
	const initialContentStateNameRef = useRef<string | null | undefined>(undefined);
	const [editorDocumentSnapshot, captureEditorDocumentSnapshot] = useEditorDocumentSnapshot();

	const content = useFragment<useContentReviewerNudgeOnCriteria_content$key>(
		graphql`
			fragment useContentReviewerNudgeOnCriteria_content on Content
			@argumentDefinitions(isDraft: { type: "Boolean!" }) {
				id
				contentState(isDraft: $isDraft) {
					id
					name
					unlocalizedName
				}
			}
		`,
		contentFragmentRef,
	);

	const currentContentStateName =
		content?.contentState?.unlocalizedName ?? content?.contentState?.name ?? null;
	const initialContentStateName = initialContentStateNameRef.current;

	useEffect(() => {
		if (initialContentStateNameRef.current === undefined && content != null) {
			initialContentStateNameRef.current = currentContentStateName;
		}
	}, [currentContentStateName, content]);

	const localizedTriggeringStateName = formatMessage(contentReviewerNudgeI18n.readyForReviewState);
	const matchesTriggeringState = (value: string | null | undefined): boolean =>
		value === TRIGGERING_CONTENT_STATE_NAME || value === localizedTriggeringStateName;

	const hasTransitionedToTriggeringState =
		initialContentStateName !== undefined &&
		!matchesTriggeringState(initialContentStateName) &&
		matchesTriggeringState(currentContentStateName);

	// Capture the document only once the page becomes a review candidate (eligible + transitioned to
	// the triggering state), so we evaluate the word-count gate against the content at that moment.
	const shouldCaptureDocument = isEligible && hasTransitionedToTriggeringState;
	useEffect(() => {
		if (shouldCaptureDocument) {
			captureEditorDocumentSnapshot();
		}
	}, [shouldCaptureDocument, captureEditorDocumentSnapshot]);

	const wordCount = useMemo(
		() => (editorDocumentSnapshot ? countWordsInAdf(editorDocumentSnapshot) : null),
		[editorDocumentSnapshot],
	);
	const hasEnoughWords = wordCount !== null && wordCount >= MIN_WORD_COUNT;

	return isEligible && hasTransitionedToTriggeringState && hasEnoughWords;
};
